/**
 * Route guards for the authenticated surfaces.
 *
 * These are the client-side equivalent of what the Next.js layouts did in an
 * `await` before rendering: resolve the session, resolve which surface the
 * account is actually allowed, and bounce anyone who doesn't belong. Running
 * them in `beforeLoad` preserves the important property — the check completes
 * *before* the route's component mounts, so a protected surface never paints
 * for an account that may not see it.
 *
 * What changed when the apps were split across origins: a guard now only ever
 * redirects *within the app it is running in*. `activeView` is resolved against
 * `servedViews()`, so the surfaces this app doesn't host are invisible to it.
 * Two consequences, both deliberate:
 *
 *  - an admin who signs in on the customer app stays there, in the HR
 *    workspace, instead of being thrown to the admin dashboard; reaching the
 *    admin dashboard is an explicit navigation (the view switcher, or the URL);
 *  - a login that isn't platform staff has no surface at all on the admin
 *    dashboard, and that app treats it exactly as it treats a signed-out
 *    visitor rather than bouncing it to the customer app. The session itself
 *    stays valid — the customer app is unaffected.
 *
 * As before, this is a UX boundary and not the security boundary: every admin
 * and workspace API re-checks the same grants server-side.
 */
import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";

import { type LocalAuthContext, loadAuthContext } from "./session";
import { VIEW_HOME, type ViewMode } from "./view-mode";

/**
 * Requires a session and that the account's active surface is `expected`.
 *
 * @param signInPath where to send a visitor this app has no surface for. Each
 *   app hosts its own auth routes, so this is app-local by design.
 */
export async function requireView(
  queryClient: QueryClient,
  expected: ViewMode,
  signInPath: string,
  currentHref: string,
): Promise<LocalAuthContext> {
  const auth = await loadAuthContext(queryClient);

  // No session — or a session this app hosts no surface for. The second case is
  // intentionally indistinguishable from the first: a login that isn't platform
  // staff must experience the admin dashboard as if it were signed out. The
  // `denied` flag only changes the copy on the sign-in screen, so someone who
  // typed an admin URL is told why the form is in front of them.
  if (!auth || auth.view === null) {
    throw redirect({
      to: signInPath,
      search: { redirect: currentHref, denied: auth ? "1" : undefined },
    });
  }

  // Honor the chosen surface among the ones this app serves: an admin may opt
  // into the HR workspace, and an account whose active view isn't `expected` is
  // sent to its own home — always an in-app path now, never another origin. The
  // view is re-derived from live capabilities, so the `hr_view` cookie remains
  // a preference and never a privilege.
  if (auth.view !== expected) {
    throw redirect({ to: VIEW_HOME[auth.view] });
  }

  // Rebuilt rather than returned as-is: the check above narrows `auth.view`,
  // but not the object holding it.
  return { ...auth, view: auth.view };
}

/**
 * For the auth routes: sends an already-signed-in visitor to their home surface
 * instead of showing them a sign-in form.
 *
 * Returns without redirecting when this app serves no surface the account may
 * use, so the admin dashboard shows its sign-in form to a non-staff session
 * rather than looping it back into a guard that would only bounce it here again.
 */
export async function redirectIfSignedIn(queryClient: QueryClient): Promise<void> {
  const auth = await loadAuthContext(queryClient);
  if (!auth || auth.view === null) return;
  throw redirect({ to: VIEW_HOME[auth.view] });
}
